import React, { useState, useEffect, useRef } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { Row, Empty, Tooltip, Divider, Button } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { sanitizeString } from 'utils/xss';
import {
  DeleteIcon,
  PlusCircleIcon,
  DropdownIcon,
  VariablesEditIcon,
  PlusIcon,
} from 'components/Icons';
import EditableSelect from 'components/EditableSelect';
import Input from 'components/Input';
import helpers from 'helpers/proposal';
import Price from './Price';
import Super from './Super';
import Miles from './Miles';
import utils from 'utils/utils';

/**
 * Sortlist component
 * @param {Object} prop - Proposal object
 * @param {boolean} price - Whether the proposal has a price
 * @param {Function} onlistChange - Function to change the list
 * @param {string} curr - Current currency
 * @param {Function} configText - Function to configure text
 * @param {Function} transform - Function to transform the list
 * @param {string} valuePayemntType - Type of payment
 * @param {string} tabType - Type of tab
 * @param {boolean} quantity - Whether the proposal has a quantity
 * @param {Array} listItem - List of items
 * @param {string} t - Type of tab
 * @param {string} stab - State of the tab
 * @param {Object} chosen - Chosen items
 * @param {Function} changeTabName - Function to change the tab name
 * @param {Function} saveTabName - Function to save the tab name
 * @param {Function} getpkgname - Function to get the package name
 * @param {Function} delOption - Function to delete an option
 * @param {Function} handleValueOptionsDropdown - Function to handle the value options dropdown
 * @param {Function} dispatch - Function to dispatch the chosen items
 * @param {Function} calculateDiscount - Function to calculate the discount
 * @param {Function} calculateTax - Function to calculate the tax
 * @param {Function} calcDiscPrice - Function to calculate the discount price
 * @param {Function} setOpenDrawer - Function to set the open drawer
 * @param {Function} editColumnName - Function to edit the column name
 * @param {Function} addOrRemoveColumn - Function to add or remove a column
 * @param {Function} editCustomColumnValue - Function to edit the custom column value
 * @param {Object} selectedDeliverable - Selected deliverable
 * @param {Function} setSelectedDeliverable - Function to set the selected deliverable
 * @param {Function} setSidebarCollapsed - Function to set the sidebar collapsed
 * @param {Function} setShowSaveDeliverableModal - Function to set the show save deliverable modal
 * @param {Function} handleManageImages - Function to handle the manage images
 * @param {boolean} showOrHidesUploadModal - Whether to show or hide the upload modal
 * @param {Function} setShowOrHidesUploadModal - Function to set the show or hide upload modal
 */
const Sortlist = ({
  prop,
  price,
  onlistChange,
  curr,
  configText,
  transform,
  selectable,
  valuePayemntType,
  tabType,
  quantity,
  listItem,
  t,
  state: { stab },
  chosen,
  changeTabName,
  saveTabName,
  getpkgname,
  delOption,
  handleValueOptionsDropdown,
  dispatch,
  calculateDiscount,
  calculateTax,
  calcDiscPrice,
  setOpenDrawer,
  editColumnName,
  addOrRemoveColumn,
  editCustomColumnValue,
  selectedDeliverable,
  setSelectedDeliverable,
  setSidebarCollapsed,
  setShowSaveDeliverableModal,
  handleManageImages,
  showOrHidesUploadModal,
  setShowOrHidesUploadModal,
}) => {
  const [list, setList] = useState(listItem);
  const [showenDescription, setShowenDescription] = useState([]);
  const [showenDiscount, setShowenDiscount] = useState([]);
  const [presentPrice] = useState('');
  const [, setCurrentPrice] = useState('');

  const prevProps = useRef(listItem);

  useEffect(() => {
    const prevLateVal = prevProps.current;
    const nextLateVal = listItem;

    if (typeof nextLateVal !== 'undefined' && prevLateVal !== nextLateVal) {
      setList(listItem);
    }
  }, [listItem]);

  /**
   * Edit time
   * @param {Object} x - List item
   * @returns {Function} - Function to edit time
   */
  const editTime = (x) => {
    return (e) => {
      _.each(list, (y) => {
        if (x.id === y.id) {
          x.time = sanitizeString(e.target.value);
        }
      });
      onlistChange(list);
    };
  };

  /**
   * Edit super
   * @param {Object} x - List item
   * @returns {Function} - Function to edit super
   */
  const editSuper = (x) => {
    return (e) => {
      _.each(list, (y, i) => {
        if (x.id === y.id) {
          x.super = { text: sanitizeString(e.target.value), order: i };
        }
      });

      onlistChange(list);
      if (e?.target?.style) {
        e.target.style.height = '5px';
        e.target.style.height = `${e.target.scrollHeight || 0}px`;
      }
    };
  };

  /**
   * Edit price
   * @param {Object} x - List item
   * @returns {Function} - Function to edit price
   */
  const editPrice = (x) => {
    return (v, isBlur) => {
      // preceding '-' supported
      v =
        prop.priceSeperator === '.'
          ? v.replace(/(?!^-)[^0-9,]/g, '')
          : v.replace(/(?!^-)[^0-9.]/g, '');

      if (isBlur) {
        // remove -, and replace -0 with 0 when blurred
        // comma is the decimal separator when priceSeperator is '.', normalise before parsing
        const vnum = Number(prop.priceSeperator === '.' ? v.replaceAll(',', '.') : v);
        if (isNaN(vnum)) {
          v = '';
        } else if (vnum === -0) {
          v = '0';
        }
      }

      const tempList = [...list];

      if (v !== '-' && utils.checkNan(v, prop.priceSeperator === '.')) {
        return;
      }

      let totes = 0;

      _.each(tempList, (y, index) => {
        if (x.id === y.id) {
          if (x.name === y.name) {
            setCurrentPrice(v);
          }

          const discVal = x?.discount?.value || 0;
          const discUnit = x?.discount?.unit || '%';

          if (x?.discount) {
            // for unit other than %
            if (y?.discount?.value && y?.discount?.unit !== '%') {
              // check if discount amount > price, set discount value = price
              if (discVal > v) {
                x.discount.value = 0;
              }
            }
          }
          const price = prop.priceSeperator === '.' ? v.replaceAll(',', '.') : v;
          const subTotal = calcDiscPrice(
            price * (chosen[stab]?.quantity ? x.items || 1 : 1),
            x?.discount?.value,
            discUnit,
            prop.priceSeperator === '.'
          );
          let z = subTotal.toString();
          if (x.name === y.name) {
            const _currentPrice = v;
            x.currentPrice = Number.isNaN(_currentPrice) ? 0 : _currentPrice;
          }
          if (prop.priceSeperator === '.') {
            z = z.replace('.', ',');
          }
          totes = totes + Number(z.replaceAll(',', '.'));
          chosen[stab].list[index].currentPrice = v;
          chosen[stab].list[index].price = z;
        } else {
          const discVal = y?.discount?.value || 0;
          const discUnit = y?.discount?.unit || '%';

          const price =
            prop.priceSeperator === '.'
              ? String(y?.currentPrice)?.replaceAll(',', '.') ||
                String(y?.price)?.replaceAll(',', '.')
              : y?.currentPrice || y?.price;

          const subTotal = calcDiscPrice(
            parseFloat(price * (chosen[stab]?.quantity ? y.items || 1 : 1)),
            discVal,
            discUnit,
            prop.priceSeperator === '.'
          );
          let z = subTotal.toString();
          if (prop.priceSeperator === '.') {
            z = z.replace('.', ',');
          }
          totes += Number(z.replaceAll(',', '.'));
        }
      });

      if (!chosen[stab].selectable) {
        [totes] = calculateDiscount({
          chosen,
          curr,
          totes: totes,
          totespredis: {},
          subtvalue: 0,
          discount: chosen[stab].discount,
        });

        [totes] = calculateTax({
          chosen,
          curr,
          totes: totes,
          totespredis: {},
          subtvalue: 0,
          taxes: chosen[stab].tax,
        });

        // reset the final discount to 0
        chosen[stab].total = 0;
        if (chosen[stab]?.discount?.unit !== '%' && totes < chosen[stab]?.discount?.val) {
          chosen[stab].discount.val = 0;
        }
      }

      dispatch({ type: 'setChosen', chosen });

      onlistChange(list);
    };
  };

  /**
   * Edit item discount
   * @param {Event} e - Event object
   * @param {Object} x - List item
   * @param {string} type - Type of discount
   * @param {string} value - The value to set
   * @returns {void}
   */
  const editItemDiscount = (e, x, type, value) => {
    /*
      e = event
      x = row object
      type = value | unit
      value = $ | %
    */
    // remove negative sign, convert to number
    let v =
      prop.priceSeperator === '.'
        ? e?.target?.value.replace(/[^0-9,]/g, '')
        : e?.target?.value.replace(/[^0-9.]/g, '');
    if (utils.checkNan(v, prop.priceSeperator === '.')) {
      return;
    }

    let val = helpers.onlyNumSeperator({ prop, x: v, curr });
    let fval = helpers.parseFloatCurr({ prop, x: val, curr });

    if (fval < 0) {
      fval *= -1; // convert to positive number
    }
    const tempList = list;

    let totes = 0;

    tempList.forEach((l, index) => {
      if (l.id === x.id) {
        // set discount object if not set
        if (!l.discount) {
          l.discount = { value: 0, unit: '%', type: -1 };
        }

        // set discount to 0 on toggling unit
        if (type === 'unit') {
          // set the new unit
          l.discount.unit = value;
          l.discount.value = 0;

          // x.price = calcDiscPrice(x.currentPrice || x.price, l.discount.value, l.discount.unit, prop.priceSeperator === '.');

          chosen[stab].list[index].discount = {
            unit: value,
            value: 0,
          };
        } else {
          // set price to 0, if not set
          if (!x.currentPrice || Number.isNaN(x?.currentPrice)) {
            x.currentPrice = x.price || 0;
            setCurrentPrice(x.currentPrice);
          }

          // discount should be <= 100
          if (x.discount.unit === '%' && v > 100) {
            v = 100;
          }
          // for $ type, v should <= currentPrice
          if (x.discount.unit !== '%' && fval > x.currentPrice) {
            v = 0;
          }

          // set new value
          l.discount.value = v;
          // calculate new discounted price
          const price =
            prop.priceSeperator === '.'
              ? x?.currentPrice
                ? String(x?.currentPrice)?.replaceAll(',', '.')
                : String(x?.price)?.replaceAll(',', '.')
              : x?.currentPrice || x?.price;
          const subTotal = calcDiscPrice(
            price * (chosen[stab]?.quantity ? x.items || 1 : 1),
            l.discount.value,
            l.discount.unit,
            prop.priceSeperator === '.'
          );

          let z = subTotal.toString();
          if (prop.priceSeperator === '.') {
            z = z.replace('.', ',');
          }
          chosen[stab].list[index].discount.value = v;
          chosen[stab].list[index].price = z;
          totes = totes + Number(z.replaceAll(',', '.'));
          // x.price = helpers.onlyNumSeperator({ prop, x: z, curr: propCurr }) || '';
          // totes += (x.price || 0) * (l.items || 1);
        }
      } else {
        const discVal = l?.discount?.value || 0;
        const discUnit = l?.discount?.unit || '%';

        const price =
          prop.priceSeperator === '.'
            ? String(l?.currentPrice)?.replaceAll(',', '.') ||
              String(l?.price)?.replaceAll(',', '.')
            : l?.currentPrice || l?.price;
        const subTotal = calcDiscPrice(
          price * (chosen[stab]?.quantity ? x.items || 1 : 1),
          discVal,
          discUnit,
          prop.priceSeperator === '.'
        );
        let z = subTotal.toString();
        // if (prop.priceSeperator === '.') {
        //   z = z.replace('.', ',');
        // }
        // const price = helpers.onlyNumSeperator({ prop, x: z, curr: propCurr }) || '';
        totes += (Number(z.replaceAll(',', '.')) || 0) * (l.items || 1);
      }
    });

    [totes] = calculateDiscount({
      chosen,
      curr,
      totes: totes,
      totespredis: {},
      subtvalue: 0,
      discount: chosen[stab].discount,
    });

    [totes] = calculateTax({
      chosen,
      curr,
      totes: totes,
      totespredis: {},
      subtvalue: 0,
      taxes: chosen[stab].tax,
    });

    // reset the final discount to 0
    chosen[stab].total = 0;
    if (chosen[stab]?.discount?.unit !== '%' && totes < chosen[stab]?.discount?.val) {
      chosen[stab].discount.val = 0;
    }

    dispatch({ type: 'setChosen', chosen });

    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Edit name
   * @param {Object} x - List item
   * @returns {Function} - Function to edit name
   */
  const editName = (x) => {
    return (e) => {
      const tempList = list;
      _.each(tempList, (y) => {
        if (x.id === y.id) {
          x.name = sanitizeString(e.target.value);
        }
      });
      setList(tempList);

      onlistChange(tempList);
    };
  };

  /**
   * Handle total change
   * @param {Event} e - Event object
   * @param {Object} x - List item
   * @returns {void}
   */
  const handleTotalChange = (e, x) => {
    const tempList = list.map((l) => (l.id === x.id ? { ...l, checked: e.target.checked } : l));

    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Edit checkbox option
   * @param {Object} x - List item
   * @param {Object} payload - Payload object
   * @returns {void}
   */
  const editCheckboxOption = (x, payload) => {
    const tempList = [...list];

    tempList.forEach((l) => {
      if (l.id === x.id) {
        l.checkboxOption = {
          on: payload.on,
          editableForClient: payload.editableForClient,
          markedByDefault: payload.markedByDefault,
        };
        if (typeof l.checked === 'undefined' && payload.on) {
          l.checked = payload.markedByDefault;
        } else if (payload.on) {
          l.checked = payload.markedByDefault;
        }
      }
    });

    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Remove item
   * @param {Object} x - List item
   * @returns {Function} - Function to remove item
   */
  const removeItem = (x) => {
    return () => {
      const tempList = _.union([], _.without(list, x));
      const customColumnsValuesToBeRemovedIndex = list.findIndex(
        (listItem) => listItem.id === x.id
      );
      const customColumns = chosen[stab]?.customColumns?.map((column) => {
        (column?.values || [])?.splice(customColumnsValuesToBeRemovedIndex, 1);
        return column;
      });

      chosen[stab].customColumns = customColumns;

      dispatch({ type: 'setChosen', chosen });
      setList(tempList);
      onlistChange(tempList);
    };
  };

  /**
   * Show hide description
   * @param {Object} x - List item
   * @param {string} type - Type of action
   * @returns {void}
   */
  const showHideDescription = (x, type) => {
    let tempShowenDescription = Array.from(showenDescription);
    const tempList = Array.from(list);
    if (type === 'push') tempShowenDescription.push(x.id && !x.super ? x.id : x.name);
    else {
      if (x.id) {
        tempShowenDescription = tempShowenDescription.filter((description) => description !== x.id);
      } else if (x.name) {
        tempShowenDescription = tempShowenDescription.filter(
          (description) => description !== x.name
        );
      }

      _.each(tempList, (y) => {
        if (x.id === y.id) {
          x.description = '';
        } else if (x.name === y.name) {
          x.description = '';
          y.description = '';
        }
      });

      onlistChange(tempList);
    }
    setShowenDescription(tempShowenDescription);
  };

  /**
   * Show hide discount
   * @param {Object} x - List item
   * @param {string} type - Type of action
   * @returns {void}
   */
  const showHideDiscount = (x, type) => {
    let tempShowenDiscount = Array.from(showenDiscount);
    const tempList = Array.from(list);

    if (type === 'push') tempShowenDiscount.push(x.id && !x.super ? x.id : x.name);
    else {
      if (x.id) tempShowenDiscount = tempShowenDiscount.filter((discount) => discount !== x.id);
      else if (x.name)
        tempShowenDiscount = tempShowenDiscount.filter((discount) => discount !== x.name);

      _.each(tempList, (y) => {
        if (x.id === y.id) {
          x.discount = '';
          x.price = x.currentPrice !== '' ? x.currentPrice : x.price;
        } else if (x.name === y.name) {
          x.discount = '';
          y.discount = '';
          x.price = x.currentPrice !== '' ? x.currentPrice : x.price;
        }
      });

      onlistChange(tempList);
    }

    setShowenDiscount(tempShowenDiscount);
  };

  /**
   * Edit quantity
   * @param {string} value - The value to set
   * @param {Object} x - List item
   * @param {string} type - Type of quantity
   * @returns {void}
   */
  const editQuantity = (value, x, type = 'quantity') => {
    // type = items | quantity | years
    const tempList = [...list];

    tempList.forEach((l) => {
      if (l.id === x.id) l[type] = Number(value) || '';
    });

    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Edit description
   * @param {Object} x - List item
   * @returns {Function} - Function to edit description
   */
  const editDescription = (x) => {
    return (e) => {
      const tempList = list;

      _.each(tempList, (y) => {
        if (x.id === y.id || x.name === y.name) {
          x.description = sanitizeString(e.target.value);
        }
      });

      onlistChange(tempList);
    };
  };

  /**
   * Edit thumbnail
   * @param {string} id - The ID of the thumbnail
   * @param {Array} urls - The URLs of the thumbnails
   * @returns {void}
   */
  const editThumbnail = (id, urls) => {
    const tempList = [...list];
    tempList.forEach((l) => {
      if (l.id === id) l.images = [...urls];
    });
    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Handle options dropdown
   * @param {string} value - The value to set
   * @param {string} id - The ID of the item
   * @returns {void}
   */
  const handleOptionsDropdown = (value, id) => {
    const tempList = [...list];
    tempList.forEach((l) => {
      if (l.id === id) l.type = value;
    });

    setList(tempList);
    onlistChange(tempList);
  };

  /**
   * Render super list
   * @returns {React.ReactNode} - React node
   */
  const renderSuperList = () => {
    const superTransform = transform || ((x) => x);

    const onSort = (newState) => {
      //remove falsy from newState
      const removedFalsy = newState.map((obj) => {
        Object.keys(obj).forEach((key) => {
          if (obj[key] === false) {
            delete obj[key];
          }
        });
        return obj;
      });

      if (JSON.stringify(list) !== JSON.stringify(removedFalsy)) {
        setList(newState);
        onlistChange(newState);
      }
    };

    return (
      <>
        {list.length ? (
          <ReactSortable list={list} setList={onSort} animation={400} handle=".handle">
            {_.map(list, (x, superIndex) => (
              <Super
                key={x.name + superIndex}
                x={x}
                editSuper={editSuper}
                removeItem={removeItem}
                showenDescription={showenDescription}
                superTransform={superTransform}
                editDescription={editDescription}
                showHideDescription={showHideDescription}
                language={prop?.language}
              />
            ))}
          </ReactSortable>
        ) : (
          <Empty description={'Select your strengths from the menu, or add your own'} />
        )}
        <div className="sidebar-btn below-xl super">
          <Button
            className="large-btn grey-btn"
            icon={<PlusCircleIcon />}
            onClick={() => setSidebarCollapsed(false)}>
            Add advantages
          </Button>
        </div>
      </>
    );
  };

  /**
   * Get value
   * @param {string} value - The value to get
   * @param {string} defaultValue - The default value
   * @returns {string} - The value
   */
  const getValue = (value, defaultValue) => {
    return typeof value === 'string' ? value : defaultValue;
  };

  const renderPriceTableList = () => {
    const propCurr = prop.curr || prop.currency || curr || configText('currency');
    const recurringTypes = ['Hourly', 'Monthly', 'Annually'];
    const recurring = price && quantity && list.find((x) => recurringTypes.includes(x.type));
    const customColumns = chosen[stab]?.customColumns || [];

    return (
      <div
        className={`
        ${list.length ? 'tablist-not-empty' : 'tablist-empty'}
        ${stab === t ? 'tablist-active-tab' : 'tablist-inactive-tab'} 
        ${quantity ? 'tablist-quantity' : ''} 
        ${recurring ? 'tablist-recurring' : ''}
        ${customColumns?.length ? 'tablist-custom' : ''}`}
        style={{
          '--custom-columns': `${customColumns?.length || 0}`,
        }}>
        <div className={`tablist-input-header ${!!chosen[stab]?.structured ? 'structured' : 'ss'}`}>
          <div className="tablist-label deliverable deliverable-r">
            <Input
              className="borderless-input flush"
              readOnly={true}
              value={getValue(chosen[stab]?.columnName?.deliverable, configText('Deliverable'))}
            />
            {customColumns?.length < 2 ? (
              <div className="tablist-add-deliverable">
                <Divider type="vertical" />
                <Tooltip title="Add Column">
                  <PlusIcon onClick={(e) => addOrRemoveColumn(e)} />
                </Tooltip>
                <Divider type="vertical" />
              </div>
            ) : null}
          </div>

          {customColumns?.length ? (
            <>
              {customColumns?.map((column, columnIndex) => (
                <div key={columnIndex} className="tablist-label custom">
                  <Input
                    className="flush"
                    autofocus={false}
                    maxLength={20}
                    value={getValue(column.name, configText('CustomColumnName'))}
                    onChange={(e) => editColumnName(columnIndex, e.target.value, 20, true)}
                    suffix={
                      <Tooltip
                        title="Remove Column"
                        onClick={(e) => addOrRemoveColumn(e, columnIndex)}>
                        <CloseOutlined />
                      </Tooltip>
                    }
                  />
                  <Divider type="vertical" />
                </div>
              ))}
            </>
          ) : null}
          {recurring && (
            <div className="tablist-label tablist-quantity-label quantity">
              <Input className="borderless-input flush" readOnly value="Duration" />
              <Divider type="vertical" />
            </div>
          )}
          {quantity && (
            <div className="tablist-label items">
              <Input
                className="borderless-input flush"
                readOnly={true}
                value={getValue(chosen[stab]?.columnName?.item, configText('Quantity'))}
              />
              <Divider type="vertical" />
            </div>
          )}
          <div className="tablist-label price">
            <Input
              className="borderless-input flush"
              readOnly={true}
              value={getValue(
                chosen[stab]?.columnName?.price,
                !!chosen[stab]?.structured ? configText('line total text') : configText('Price')
              )}
            />
            <Divider type="vertical" />
          </div>
          <div className="tablist-label price-options">
            <Input className="borderless-input flush" readOnly value="Payment Type" />

            {
              // show edit icon for structured
              !!chosen[stab]?.structured && (
                <Tooltip title="Rename titles">
                  <VariablesEditIcon
                    className="edit-icon"
                    onClick={() => setOpenDrawer('edit-title')}
                  />
                </Tooltip>
              )
            }
            <Divider type="vertical" />
          </div>
        </div>
        {list.length ? (
          <ReactSortable
            list={list}
            setList={(newState) => {
              if (JSON.stringify(list) !== JSON.stringify(newState)) {
                setList(newState);
                onlistChange(newState);
              }
            }}
            animation={400}
            className="sortable-container"
            handle=".handle">
            {_.map(list, (x, listIndex) => (
              <Price
                displayType="table"
                discountValue={x?.discount?.value}
                currentPrice={presentPrice}
                key={x.id}
                listIndex={`${x.id}`}
                t={t}
                price={price}
                propCurr={propCurr}
                valuePayemntType={valuePayemntType}
                x={x}
                quantity={quantity}
                selectable={selectable}
                customColumnsValues={customColumns?.map(
                  (customColumn) => customColumn.values[listIndex]
                )}
                editCustomColumnValue={(index, value) =>
                  editCustomColumnValue(index, listIndex, value)
                }
                handleTotalChange={handleTotalChange}
                editName={editName}
                configText={configText}
                editQuantity={editQuantity}
                editPrice={editPrice}
                editDescription={editDescription}
                editItemDiscount={editItemDiscount}
                removeItem={removeItem}
                showenDescription={showenDescription}
                showHideDescription={showHideDescription}
                showenDiscount={showenDiscount}
                showHideDiscount={showHideDiscount}
                handleOptionsDropdown={handleOptionsDropdown}
                editCheckboxOption={editCheckboxOption}
                globalSelectable={selectable}
                language={prop?.language}
                thumbnail={chosen[stab]?.thumbnail}
                editThumbnail={editThumbnail}
                setShowSaveDeliverableModal={setShowSaveDeliverableModal}
                selectedDeliverable={selectedDeliverable}
                setSelectedDeliverable={setSelectedDeliverable}
                handleManageImages={handleManageImages}
                showOrHidesUploadModal={showOrHidesUploadModal}
                setShowOrHidesUploadModal={setShowOrHidesUploadModal}
              />
            ))}
          </ReactSortable>
        ) : (
          <Empty description={false} />
        )}
      </div>
    );
  };

  useEffect(() => {
    // for first time allow save
    if (!stab) changeTabName(getpkgname(stab, chosen[stab], true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stab]);

  /**
   * Render price value list
   * @returns {React.ReactNode} - React node
   */
  const renderPriceValueList = () => {
    const propCurr = prop.curr || prop.currency || curr || configText('currency');

    return (
      <div className="sortlist-container">
        <div className="sortlist-price-tab-options">
          <Row className="sortlist-price-fields">
            <Input
              value={getpkgname(stab, chosen[stab])}
              onChange={changeTabName}
              onBlur={saveTabName}
            />

            <EditableSelect
              items={['One-Time', 'Monthly', 'Annually', 'Hourly']}
              selectClassName="price-options-dropdown"
              inputClassName="price-options-input"
              placeholder="Payment Type"
              x={chosen[stab] || {}}
              handleOptionsDropdown={handleValueOptionsDropdown}
              configText={configText}
              dropdownClassName={
                prop?.language === 'hebrew' || prop?.language === 'arabic'
                  ? 'wizard-language-rtl'
                  : ''
              }
              suffixIcon={<DropdownIcon />}
            />
          </Row>
          <Button className="white-btn medium-btn sortlist-price-delete" onClick={delOption(t)}>
            <DeleteIcon />
          </Button>
        </div>
        {list?.length ? (
          <ReactSortable
            list={list}
            setList={(newState) => {
              if (JSON.stringify(list) !== JSON.stringify(newState)) {
                setList(newState);
                onlistChange(newState);
              }
            }}
            animation={400}
            handle=".handle">
            {_.map(list, (x, listIndex) => (
              <Price
                displayType="value"
                discountValue={x?.discount?.value}
                key={x.id + listIndex}
                listIndex={`${x.id} + ${listIndex}`}
                t={t}
                stab={stab}
                price={price}
                propCurr={propCurr}
                valuePayemntType={valuePayemntType}
                x={x}
                quantity={quantity}
                selectable={selectable}
                handleTotalChange={handleTotalChange}
                editName={editName}
                configText={configText}
                editQuantity={editQuantity}
                editPrice={editPrice}
                editDescription={editDescription}
                removeItem={removeItem}
                showenDescription={showenDescription}
                setShowenDescription={setShowenDescription}
                showHideDescription={showHideDescription}
                handleOptionsDropdown={handleOptionsDropdown}
                editCheckboxOption={editCheckboxOption}
                globalSelectable={selectable}
                language={prop?.language}
                thumbnail={chosen[stab]?.thumbnail}
                editThumbnail={editThumbnail}
                setShowSaveDeliverableModal={setShowSaveDeliverableModal}
                selectedDeliverable={selectedDeliverable}
                setSelectedDeliverable={setSelectedDeliverable}
                handleManageImages={handleManageImages}
                showOrHidesUploadModal={showOrHidesUploadModal}
                setShowOrHidesUploadModal={setShowOrHidesUploadModal}
              />
            ))}
          </ReactSortable>
        ) : (
          <Empty description={false} />
        )}
      </div>
    );
  };

  /**
   * Render miles list
   * @returns {React.ReactNode} - React node
   */
  const renderMilesList = () => {
    if (stab === t)
      return (
        <div className={stab === t ? 'tablist-active-tab' : 'tablist-inactive-tab'}>
          {list.length ? (
            <ReactSortable
              list={list}
              setList={(newState) => {
                if (JSON.stringify(list) !== JSON.stringify(newState)) {
                  setList(newState);
                  onlistChange(newState);
                }
              }}
              animation={400}
              handle=".handle">
              {_.map(list, (x, milesIndex) => (
                <Miles
                  key={x.id + milesIndex}
                  x={x}
                  editName={editName}
                  editTime={editTime}
                  removeItem={removeItem}
                  showenDescription={showenDescription}
                  editDescription={editDescription}
                  showHideDescription={showHideDescription}
                />
              ))}
            </ReactSortable>
          ) : (
            <Empty description={false} />
          )}
          <div className="sidebar-btn below-xl miles">
            <Button
              className="large-btn grey-btn"
              icon={<PlusCircleIcon />}
              onClick={() => setSidebarCollapsed(false)}>
              Add milestones
            </Button>
          </div>
        </div>
      );
    return null;
  };

  /**
   * Render sortlist
   * @returns {React.ReactNode} - React node
   */
  if (tabType === 'delivs') {
    return price ? renderPriceTableList() : renderPriceValueList();
  }

  if (tabType === 'miles') {
    return renderMilesList();
  }

  return renderSuperList();
};

/**
 * Default props for the sortlist
 * @returns {Object} - Default props
 */
Sortlist.defaultProps = {
  price: false,
  curr: '',
  selectable: '',
  valuePayemntType: false,
  quantity: false,
  selectedDeliverable: null,
  transform: () => {},
  dispatch: () => {},
  addOrRemoveColumn: () => {},
  editCustomColumnValue: () => {},
  setSelectedDeliverable: () => {},
};

/**
 * Prop types for the sortlist
 * @returns {Object} - Prop types
 */
Sortlist.propTypes = {
  listItem: PropTypes.instanceOf(Array).isRequired,
  prop: PropTypes.instanceOf(Object).isRequired,
  price: PropTypes.bool,
  onlistChange: PropTypes.func.isRequired,
  curr: PropTypes.string,
  configText: PropTypes.instanceOf(Object).isRequired,
  transform: PropTypes.func,
  dispatch: PropTypes.func,
  tabType: PropTypes.string.isRequired,
  selectable: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  valuePayemntType: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  quantity: PropTypes.bool,
  selectedDeliverable: PropTypes.instanceOf(Object),
  addOrRemoveColumn: PropTypes.func,
  editCustomColumnValue: PropTypes.func,
  setSelectedDeliverable: PropTypes.func,
};

export default Sortlist;
